import { ErrorMessage, Field } from 'formik';
import {
    Col,
    Form,
    FormControl,
    InputGroup,
    OverlayTrigger,
    Popover,
    Row,
    Spinner,
    Stack,
    Tooltip
} from 'react-bootstrap';
import { BsCurrencyDollar, BsInfoCircle } from 'react-icons/bs';
import { useToast } from '../../../../../../context/ToastContext';
import { useGetThemesSimpleQuery } from '../../../../../../services/endpoints/content/theme';
import { Theme } from '../../../../../../types/theme.type';
import { getErrorMessage } from '../../../../../../utils/utils';
import { StepProps } from '../AddEditSession';
import '../SessionDetail.css';
import { NumberInput} from "../../../../../../components/inputs/NumberInput";
import { FormLabel, FormStepHeader } from '../../../../../../components/forms';
type InvoiceType = 'per_student' | 'flat_rate';

const getPriceLabel = (invoiceType: InvoiceType): string => {
  switch (invoiceType) {
    case 'flat_rate':
      return 'Flat Rate Tuition Per Class';
    case 'per_student':
      return 'Price per student, per class';
    default:
      return 'Tuition Price';
  }
};

const getPriceHelperText = (
  invoiceType: InvoiceType,
  classes: any[],
  pricePerClass: number
): string => {
  switch (invoiceType) {
    case 'flat_rate':
      return `Flat rate per class: $${pricePerClass.toFixed(2)}`;
    case 'per_student':
      return `${classes?.length ?? 0} scheduled classes x $${pricePerClass.toFixed(2)} = $${((classes?.length ?? 0) * pricePerClass).toFixed(2)}`;
    default:
      return '';
  }
};

export const SessionDetailsForm = ({
  touched,
  errors,
  readOnly,
  values,
  setFieldValue
}: StepProps) => {
  const { data, error, isLoading } = useGetThemesSimpleQuery();
  const toast = useToast();

  if (isLoading) {
    return (
      <div className="w-100 vh-100 d-inline-flex">
        <Spinner
          variant="primary"
          className="m-auto"
        />
      </div>
    );
  }

  if (error || !data) {
    toast.addToast(getErrorMessage(error) ?? 'Error loading themes', 'error');
  }

  return (
    <Row>
      <Col xs={12}>
        <FormStepHeader title="Session Details" showRequiredLegend={!readOnly} />
      </Col>
      <Col
        xs={12}
        lg={6}
      >
        <Stack gap={3}>
          <Form.Group controlId="title">
            <FormLabel className="d-flex align-items-center gap-2 mb-1" required>
              Title
                <OverlayTrigger
                    placement="right"
                    trigger={["hover", "focus"]}
                    overlay={
                        <Popover
                            id="title-popover"
                            style={{
                                maxWidth: '360px',
                                border: '1px solid #dae39a',
                                borderRadius: '8px',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                            }}
                        >
                            <Popover.Body style={{
                                fontSize: '0.825rem',
                                color: '#333',
                                padding: '12px 16px',
                                lineHeight: '1.6',
                            }}>
                                <div>Session titles should contain the complete venue name, season, and year. For example:</div>
                                <div style={{ margin: '8px 0' }}>
                                    <div><strong>ASE Title:</strong> Dupage Montessori School Spring 2024 K-2nd</div>
                                    <div><strong>Camp Title:</strong> Boulder Country Day School Summer 2024 K-5th</div>
                                </div>
                                <div>See the DASH Handbook for more guidelines/examples!</div>
                            </Popover.Body>
                        </Popover>
                    }
                    popperConfig={{ modifiers: [{ name: 'offset', options: { offset: [0, 8] } }] }}
                >
                <span style={{ cursor: 'pointer', color: '#6c757d', display: 'inline-flex', alignItems: 'center' }}>
                  <BsInfoCircle size={14} />
                </span>
              </OverlayTrigger>
            </FormLabel>
            <Field
              name="title"
              type="text"
              as={Form.Control}
              isInvalid={touched.title && !!errors.title}
              disabled={readOnly}
              aria-required
            />
            <ErrorMessage name="title">
              {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
            </ErrorMessage>
          </Form.Group>

          <Form.Group controlId="is_online">
            <FormLabel required>Program Type</FormLabel>
            <Field
              name="is_online"
              as={Form.Select}
              isInvalid={touched.is_online && !!errors.is_online}
              disabled={readOnly}
              aria-required
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                const { value } = e.target;
                setFieldValue!('is_online', value);
                if (value !== 'online') setFieldValue!('video_url', undefined);
              }}
            >
              <option value="">-</option>
              <option value="in_person">In-person</option>
              <option value="online">Online</option>
            </Field>
            <ErrorMessage name="is_online">
              {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
            </ErrorMessage>
          </Form.Group>

          {values?.is_online === 'online' && (
            <Form.Group controlId="video_url">
              <FormLabel required>Online Class Link</FormLabel>
              <Field
                name="video_url"
                type="text"
                as={Form.Control}
                isInvalid={touched.video_url && !!errors.video_url}
                disabled={readOnly}
                aria-required
              />
              <ErrorMessage name="video_url">
                {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
              </ErrorMessage>
            </Form.Group>
          )}

          <Form.Group controlId="payment_type">
            <FormLabel required>Payment Type</FormLabel>
            <Field
              name="payment_type"
              as={Form.Select}
              isInvalid={touched.payment_type && !!errors.payment_type}
              disabled={readOnly}
              aria-required
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                const { value } = e.target;
                setFieldValue!('payment_type', value);
                if (value === 'onsite') {
                  setFieldValue!('video_url', undefined);
                  setFieldValue!('can_be_prorated', undefined);
                  setFieldValue!('invoice_type', 'per_student');
                } else {
                  // Clear leftover onsite invoice_type so the price label resets.
                  setFieldValue!('invoice_type', undefined);
                  if (value === 'website') {
                    setFieldValue!('can_be_prorated', false);
                  } else {
                    setFieldValue!('video_url', undefined);
                    setFieldValue!('can_be_prorated', undefined);
                  }
                }
              }}
            >
              <option value="">-</option>
              <option value="website">Website</option>
              <option value="onsite">Onsite</option>
            </Field>
            <ErrorMessage name="payment_type">
              {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
            </ErrorMessage>
          </Form.Group>

          {values?.payment_type === 'onsite' && (
            <Form.Group controlId="invoice_type">
              <FormLabel required>Invoice Type</FormLabel>
              <Field
                name="invoice_type"
                as={Form.Select}
                disabled={readOnly}
                aria-required
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                  const { value } = e.target;
                  setFieldValue!('invoice_type', value);
                  // Reset price_per_class when changing invoice type if needed
                  // setFieldValue!('price_per_class', '');
                }}
              >
                <option value="">-</option>
                <option value="per_student">Per student</option>
                <option value="flat_rate">Flat rate</option>
              </Field>
              <ErrorMessage name="invoice_type">
                {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
              </ErrorMessage>
            </Form.Group>
          )}

          <Form.Group controlId="price_per_class">
            <FormLabel className="d-flex align-items-center gap-2 mb-1" required>
              {getPriceLabel(values?.invoice_type as InvoiceType)}
              {values?.payment_type === 'website' && (
                <OverlayTrigger
                  placement="right"
                  trigger={['hover', 'focus']}
                  overlay={
                    <Tooltip id="tuition-price-website-note">
                      Tuition is per-student, per-class
                    </Tooltip>
                  }
                >
                  <span
                    tabIndex={0}
                    aria-label="Tuition is per-student, per-class"
                    style={{ cursor: 'pointer', color: '#6c757d', display: 'inline-flex', alignItems: 'center' }}
                  >
                    <BsInfoCircle size={14} />
                  </span>
                </OverlayTrigger>
              )}
            </FormLabel>
            <InputGroup>
              <InputGroup.Text>
                <BsCurrencyDollar color="#5E6469" />
              </InputGroup.Text>
              <Field
                name="price_per_class"
                as={NumberInput}
                isInvalid={touched.price_per_class && !!errors.price_per_class}
                disabled={readOnly}
                aria-required
              />
            </InputGroup>
            <Form.Text>
              {getPriceHelperText(
                values?.invoice_type as InvoiceType,
                values?.classes ?? [],
                +(values?.price_per_class ?? 0)
              )}
            </Form.Text>
            <ErrorMessage name="price_per_class">
              {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
            </ErrorMessage>
          </Form.Group>

          {values!.payment_type === 'website' && (
            <Form.Group
              className="fw-semibold"
              controlId="can_be_prorated"
            >
              <Field
                as={Form.Check}
                label="Prorate tuition"
                name="can_be_prorated"
                checked={values?.can_be_prorated}
                disabled={readOnly}
              />
            </Form.Group>
          )}
        </Stack>
      </Col>

      <Col
        xs={12}
        lg={6}
      >
        <Stack gap={3}>
          <Form.Group controlId="course_type">
            <FormLabel required>Session Type</FormLabel>
            <Field
              name="course_type"
              as={Form.Select}
              isInvalid={touched.course_type && !!errors.course_type}
              disabled={readOnly}
              aria-required
            >
              <option value="">-</option>
              <option value="after_school">After School</option>
              <option value="camp">Camp</option>
              <option value="event">Event</option>
              <option value="silver_spatulas">Silver Spatulas</option>
            </Field>
            <ErrorMessage name="course_type">
              {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
            </ErrorMessage>
          </Form.Group>

          <Form.Group controlId="theme_id">
            <FormLabel required>Theme</FormLabel>
            <Field
              name="theme_id"
              as={Form.Select}
              isInvalid={touched.theme_id && !!errors.theme_id}
              disabled={readOnly}
              aria-required
            >
              <option value="">-</option>
              {[...(data ?? [])].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')).map((theme: Theme) => (
                <option
                  key={theme.id}
                  value={theme.id}
                >
                  {theme.name}
                </option>
              ))}
            </Field>
            <ErrorMessage name="theme_id">
              {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
            </ErrorMessage>
          </Form.Group>

          <Form.Group controlId="status">
            <FormLabel required>Status</FormLabel>
            <Field
              name="status"
              as={Form.Select}
              isInvalid={touched.status && !!errors.status}
              disabled={readOnly}
              aria-required
            >
              <option value="">-</option>
              <option value="open">Open</option>
              <option value="offline">Offline</option>
              <option value="cancelled">Cancelled</option>
            </Field>
            <ErrorMessage name="status">
              {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
            </ErrorMessage>
          </Form.Group>

          <div>
            <Form.Group
              className="fw-semibold"
              controlId="is_hidden"
            >
              <Field
                as={Form.Check}
                label="Not visible on the front-end of the site"
                name="is_hidden"
                disabled={readOnly}
                checked={values?.is_hidden}
              />
            </Form.Group>

            <Form.Group
              className="fw-semibold"
              controlId="opts_out_of_roster_emails"
            >
              <Field
                as={Form.Check}
                label="Opts out of roster emails"
                name="opts_out_of_roster_emails"
                disabled={readOnly}
                checked={values?.opts_out_of_roster_emails}
              />
            </Form.Group>
          </div>
        </Stack>
      </Col>
    </Row>
  );
};
